import React, { Component, memo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { CheckCircle } from 'lucide-react';
import {
  FilePickerForm,
  TenderCategoryForm,
  TenderForm,
  TenderFormData,
  TenderTypeForm,
  VehicleCreateForm,
} from '@app/forms';
import { convertToDate, setMask } from '@app/helpers';
import { TenderCategory, TenderTypes } from '@app/models';
import { createTender, createVehicle } from '@app/api';
import { VehicleFormData } from 'forms/VehicleCraete/VehicleCreate';
import { EventEmitter } from 'EventEmitter';

interface Props {
  visible: boolean;
  companyId: string | null;
  closeModal: () => void;
  tenderCategory?: TenderCategory;
  data?: Partial<TenderFormData>;
  onSuccess?: () => void;
}

interface State {
  currentStep: number;
  maxCurrentStep: number;
  files: File[];
  data: TenderFormData;
  pending: boolean;
  tenderCategory: TenderCategory | null;
  tenderType: TenderTypes | null;
  vehicle: VehicleFormData | null;
  showSuccessNotification?: boolean;
}

class ModalTenderCreate extends Component<Props, State> {
  private contentRef = React.createRef<HTMLDivElement>();

  steps = [
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 0 ? 0 : currentStep,
        }));
      },
      title: 'Выберите предмет объявления',
    },
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 1 ? 1 : currentStep,
        }));
      },
      title: 'Выберите тип объявления',
    },
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 2 ? 2 : currentStep,
        }));
      },
      title: 'Заполните данные',
    },
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 3 ? 3 : currentStep,
        }));
      },
      title: 'Загрузите документы и файлы',
    },
  ];

  getInitialState = (): State => {
    const { tenderCategory } = this.props;

    return {
      currentStep: tenderCategory
        ? tenderCategory === TenderCategory.VEHICLE
          ? 1
          : 2
        : 0,
      maxCurrentStep: tenderCategory
        ? tenderCategory === TenderCategory.VEHICLE
          ? 1
          : 2
        : 0,
      files: [],
      pending: false,
      data: {} as TenderFormData,
      tenderCategory: tenderCategory || null,
      tenderType:
        tenderCategory === TenderCategory.VEHICLE ? null : TenderTypes.REQUEST,
      vehicle: null,
    };
  };

  state: State = this.getInitialState();

  componentWillReceiveProps(nextProps: Props) {
    const { tenderCategory, visible } = nextProps;

    if (tenderCategory !== this.props.tenderCategory) {
      this.setState({
        currentStep: tenderCategory
          ? tenderCategory === TenderCategory.VEHICLE
            ? 1
            : 2
          : 0,
        maxCurrentStep: tenderCategory
          ? tenderCategory === TenderCategory.VEHICLE
            ? 1
            : 2
          : 0,
        tenderCategory: tenderCategory || null,
        tenderType:
          tenderCategory === TenderCategory.VEHICLE
            ? null
            : TenderTypes.REQUEST,
      });
    }

    if (!visible) {
      this.setState(this.getInitialState());
    }
  }

  createTender = () => {
    const { data, files, tenderType, tenderCategory, vehicle } = this.state;

    if (tenderType === TenderTypes.REQUEST) {
      createTender({
        okedCode: data.oked,
        katoCode: data.kato,
        name: data.name,
        description: data.description,
        price: data.price,
        deliveryDate: data.deliveryDate!.toString(),
        endDate: data.endDate!.toString(),
        files,
        type: TenderTypes.REQUEST,
        category: tenderCategory!,
        requirements: data.requirements!.join(','),
        companyId: this.props.companyId,
        measure: data.measure,
      })
        .then(() => {
          this.setState({
            currentStep: 0,
            maxCurrentStep: 0,
            files: [],
            pending: false,
            data: {} as TenderFormData,
            tenderCategory: null,
            tenderType: null,
          });

          EventEmitter.emit('updateHomeSearch');
          this.props.closeModal();
          if (typeof this.props.onSuccess === 'function') {
            this.props.onSuccess();
          }
        })
        .catch(() => {
          this.setState({
            pending: false,
          });
        });

      return;
    }

    if (tenderCategory === TenderCategory.VEHICLE) {
      createVehicle({
        name: vehicle!.name,
        description: vehicle!.description,
        price: vehicle!.price,
        files,
        brand: vehicle!.brand,
        type: vehicle!.type,
        companyId: this.props.companyId,
        dateOfIssue: convertToDate(
          setMask(vehicle!.dateOfIssue, '##.##.####')
        ).toJSON(),
        katoCode: vehicle!.kato,
        measure: vehicle?.measure ? vehicle.measure.value : null,
        okedCode: vehicle?.oked ?? null,
      })
        .then(() => {
          this.setState({
            currentStep: 0,
            maxCurrentStep: 0,
            files: [],
            pending: false,
            data: {} as TenderFormData,
            tenderCategory: null,
            tenderType: null,
          });

          EventEmitter.emit('updateHomeSearch');
          this.props.closeModal();
          if (typeof this.props.onSuccess === 'function') {
            this.props.onSuccess();
          }
        })
        .catch(() => {
          this.setState({
            pending: false,
          });
        });

      return;
    }
  };

  onSubmitTenderCategoryForm = (
    tenderCategory: TenderCategory,
    isSubmit: boolean
  ) => {
    this.setState(
      (prevState) => {
        const settableStep = isSubmit
          ? tenderCategory === TenderCategory.VEHICLE
            ? 1
            : 2
          : prevState.currentStep;

        return {
          tenderCategory,
          currentStep: settableStep,
          maxCurrentStep:
            settableStep >= prevState.maxCurrentStep
              ? settableStep
              : prevState.maxCurrentStep,
          tenderType:
            tenderCategory === TenderCategory.VEHICLE
              ? null
              : TenderTypes.REQUEST,
        };
      },
      () => {
        this.scrollToTop();
      }
    );
  };

  onSubmitTenderTypeForm = (tenderType: TenderTypes, isSubmit: boolean) => {
    this.setState(
      (prevState) => {
        const settableStep = isSubmit ? 2 : prevState.currentStep;

        return {
          tenderType,
          currentStep: settableStep,
          maxCurrentStep:
            settableStep >= prevState.maxCurrentStep
              ? settableStep
              : prevState.maxCurrentStep,
        };
      },
      () => {
        this.scrollToTop();
      }
    );
  };

  onSubmitTenderDataForm = (data: TenderFormData) => {
    this.setState(
      (prevState) => ({
        currentStep: 3,
        data,
        maxCurrentStep:
          prevState.maxCurrentStep < 3 ? 3 : prevState.maxCurrentStep,
      }),
      () => {
        this.scrollToTop();
      }
    );
  };

  onSubmitVehicleDataForm = (vehicle: VehicleFormData) => {
    this.setState(
      (prevState) => ({
        currentStep: 3,
        vehicle,
        maxCurrentStep:
          prevState.maxCurrentStep < 3 ? 3 : prevState.maxCurrentStep,
      }),
      () => {
        this.scrollToTop();
      }
    );
  };

  onSubmitFiles = (files: File[]) => {
    this.setState(
      {
        files,
        pending: true,
      },
      () => {
        this.createTender();
      }
    );
  };

  scrollToTop = () => {
    if (this.contentRef.current) {
      this.contentRef.current.scrollTop = 0;
    }
  };

  renderStepper = () => {
    const { currentStep, maxCurrentStep } = this.state;
    const { closeModal } = this.props;

    return (
      <div className="border-b p-4">
        <div className="flex items-center justify-between p-2 mb-4">
          <DialogTitle className="text-lg font-medium">
            Создать объявление
          </DialogTitle>
        </div>
        <div className="flex items-center justify-center space-x-4">
          {this.steps.map((step, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div
                className={`flex items-center cursor-pointer ${
                  maxCurrentStep >= index
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
                onClick={step.onClick}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === index
                      ? 'bg-primary text-primary-foreground'
                      : maxCurrentStep >= index
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {maxCurrentStep > index ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span className="ml-2 text-sm font-medium hidden md:block">
                  {step.title}
                </span>
              </div>
              {index < this.steps.length - 1 && (
                <div className="w-8 h-px bg-border" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  render() {
    const { visible, closeModal } = this.props;
    const { currentStep, tenderCategory, tenderType, pending } = this.state;

    if (!visible) {
      return null;
    }

    return (
      <Dialog open={visible} onOpenChange={closeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col">
          <DialogHeader className="p-0 flex-shrink-0">
            {this.renderStepper()}
          </DialogHeader>
          <div
            ref={this.contentRef}
            className="bg-gray-50 p-6 overflow-y-auto flex-1 space-y-4"
          >
            {currentStep === 0 && (
              <Card className={''}>
                <CardContent className="p-6">
                  <TenderCategoryForm
                    defaultExpanded={currentStep === 0}
                    value={tenderCategory}
                    onSubmit={this.onSubmitTenderCategoryForm}
                  />
                </CardContent>
              </Card>
            )}
            {currentStep === 1 && (
              <Card className={''}>
                <CardContent className="p-0">
                  <TenderTypeForm
                    value={tenderType}
                    defaultExpanded={currentStep === 1}
                    onSubmit={this.onSubmitTenderTypeForm}
                  />
                </CardContent>
              </Card>
            )}
            {currentStep === 2 && tenderType === TenderTypes.REQUEST && (
              <Card className={''}>
                <CardContent className="p-0">
                  <TenderForm
                    data={this.props.data}
                    tenderCategory={tenderCategory}
                    defaultExpanded={currentStep === 2}
                    onSubmit={this.onSubmitTenderDataForm}
                  />
                </CardContent>
              </Card>
            )}
            {currentStep === 2 && tenderType === TenderTypes.OFFER && (
              <Card className={''}>
                <CardContent className="p-6">
                  <VehicleCreateForm
                    defaultExpanded={currentStep === 2}
                    onSubmit={this.onSubmitVehicleDataForm}
                  />
                </CardContent>
              </Card>
            )}
            {currentStep === 3 && (
              <Card className={''}>
                <CardContent className="p-6">
                  <FilePickerForm
                    pending={pending}
                    required={tenderType === TenderTypes.OFFER}
                    defaultExpanded={currentStep === 3}
                    onSubmit={this.onSubmitFiles}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

export default memo(ModalTenderCreate);
